import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileSpreadsheet, Printer, Receipt, CheckCircle2, AlertCircle, Play, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import ReportWorkflowTrail from '../../reports/ReportWorkflowTrail';
import ReportPeriodSelector, { getMonthDateRange, getYearDateRange } from '../../reports/ReportPeriodSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ARSummaryTab({ customers, fmt, viewingStudentId, viewingStudentName, onNavigate, onOpenSource }) {
    const now = new Date();
    const initialMonth = now.getMonth() + 1;
    const initialYear = now.getFullYear();
    const initialRange = getMonthDateRange(initialYear, initialMonth);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [viewType, setViewType] = useState('detailed'); // detailed, summary
    const [transactionType, setTransactionType] = useState('both'); // invoices, collections, both
    const [dateType, setDateType] = useState('transaction'); // transaction, posting
    const [status, setStatus] = useState('all'); // unposted, posted, cancelled, all
    const [periodMode, setPeriodMode] = useState('monthly');
    const [periodMonth, setPeriodMonth] = useState(initialMonth);
    const [periodYear, setPeriodYear] = useState(initialYear);
    
    const [filters, setFilters] = useState({
        dateFrom: initialRange.dateFrom,
        dateTo: initialRange.dateTo,
        customer: ''
    });

    const applyPeriodMode = (mode) => {
        setPeriodMode(mode);
        setFilters(prev => {
            if (mode === 'monthly') {
                return { ...prev, ...getMonthDateRange(periodYear, periodMonth) };
            }
            if (mode === 'annual') {
                return { ...prev, ...getYearDateRange(periodYear) };
            }
            return {
                ...prev,
                dateFrom: prev.dateFrom || initialRange.dateFrom,
                dateTo: prev.dateTo || initialRange.dateTo,
            };
        });
    };

    const handlePeriodMonthChange = (month) => {
        setPeriodMode('monthly');
        setPeriodMonth(month);
        setFilters(prev => ({ ...prev, ...getMonthDateRange(periodYear, month) }));
    };

    const handlePeriodYearChange = (year) => {
        setPeriodYear(year);
        setFilters(prev => ({
            ...prev,
            ...(periodMode === 'annual' ? getYearDateRange(year) : getMonthDateRange(year, periodMonth)),
        }));
    };

    const handleCustomDateChange = (field, value) => {
        setPeriodMode('custom');
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const openArTrail = (tab, label) => {
        onNavigate?.(tab);
        toast.info(`Opening ${label}. Use customer, document number, and posting status to trace this AR Summary amount back to source work.`);
    };

    const openSummarySource = (txn) => {
        const isCollection = String(txn.type || '').toLowerCase().startsWith('collection');
        const recordType = txn.record_type || (isCollection ? 'AR Collection' : 'AR Invoice');
        const sourceType = txn.source_type || (isCollection ? 'ar_collection' : 'ar_invoice');
        const workspaceTab = txn.workspace_tab || (isCollection ? 'arcollections' : 'arinvoices');
        onOpenSource?.({
            source_type: sourceType,
            workspace_tab: workspaceTab,
            record_id: txn.record_id || '',
            record_number: txn.doc_number || '',
            doc_number: txn.doc_number || '',
            record_type: recordType,
            source_location: 'AR Summary detailed row',
            source_impact: 'Posted AR source rows affect AR Summary, GL, Trial Balance, and related AR reports.',
            source_next_action: 'Review the source status, posting date, customer, amount, and GL evidence before correction.',
        });
    };
    
    // Generate AR Summary
    const handleGenerate = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            
            params.append('view', viewType);
            params.append('type', transactionType);
            params.append('date_type', dateType);
            params.append('date_from', filters.dateFrom);
            params.append('date_to', filters.dateTo);
            if (filters.customer) params.append('customer', filters.customer);
            if (status !== 'all') params.append('status', status);
            if (viewingStudentId) params.append('student_id', viewingStudentId);
            
            const response = await axios.get(`${API}/ar-summary?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setData(response.data);
            toast.success('AR Summary generated on this report tab. It summarizes AR source records for the selected filters; no invoices, collections, GL, or Trial Balance records were changed.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate AR Summary');
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="space-y-6" data-testid="ar-summary-tab">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">AR Summary</h2>
                    <p className="text-sm text-slate-500">
                        {viewingStudentName ? `Accounts Receivable Summary for ${viewingStudentName}` : 'Accounts Receivable Summary Report'}
                    </p>
                </div>
            </div>
            {viewingStudentName && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="ar-summary-student-scope">
                    Student review scope is active. This report uses AR invoices and collections from {viewingStudentName}'s workspace only.
                </div>
            )}
            <ReportWorkflowTrail
                title="AR Summary trail"
                testId="ar-summary-workflow-trail"
                scope={viewingStudentName ? `Reviewing ${viewingStudentName}` : 'Your workspace'}
                items={[
                    {
                        label: 'Source records',
                        value: 'Uses AR Invoices and AR Collections that match the selected date, customer, status, and transaction filters.'
                    },
                    {
                        label: 'Where to verify',
                        value: 'Open AR Invoices or AR Collections to inspect the original source document and its posting state.'
                    },
                    {
                        label: 'GL / Trial Balance',
                        value: status === 'posted'
                            ? 'Posted AR activity affects GL and Trial Balance.'
                            : 'Only posted rows affect GL and Trial Balance; drafts and cancelled rows are review evidence only.'
                    },
                    {
                        label: 'Next safe action',
                        value: 'Preview the report, compare outstanding to reconciliation, then correct the source document if needed.'
                    }
                ]}
            />
            
            {/* Filters Panel */}
            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4">
                {/* View Type and Transaction Type */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <Label className="text-sm font-medium">View Type</Label>
                        <div className="flex gap-2 mt-1">
                            <Button
                                variant={viewType === 'detailed' ? 'default' : 'outline'}
                                onClick={() => setViewType('detailed')}
                                className={viewType === 'detailed' ? 'bg-[#2F8FCB]' : ''}
                                size="sm"
                            >
                                Detailed
                            </Button>
                            <Button
                                variant={viewType === 'summary' ? 'default' : 'outline'}
                                onClick={() => setViewType('summary')}
                                className={viewType === 'summary' ? 'bg-[#2F8FCB]' : ''}
                                size="sm"
                            >
                                Summary
                            </Button>
                        </div>
                    </div>
                    
                    <div>
                        <Label className="text-sm font-medium">Transaction Type</Label>
                        <Select value={transactionType} onValueChange={setTransactionType}>
                            <SelectTrigger className="mt-1">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="both">Invoices & Collections</SelectItem>
                                <SelectItem value="invoices">Invoices Only</SelectItem>
                                <SelectItem value="collections">Collections Only</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    
                    <div>
                        <Label className="text-sm font-medium">Date Type</Label>
                        <div className="flex gap-2 mt-1">
                            <Button
                                variant={dateType === 'transaction' ? 'default' : 'outline'}
                                onClick={() => setDateType('transaction')}
                                className={dateType === 'transaction' ? 'bg-slate-700' : ''}
                                size="sm"
                            >
                                Transaction
                            </Button>
                            <Button
                                variant={dateType === 'posting' ? 'default' : 'outline'}
                                onClick={() => setDateType('posting')}
                                className={dateType === 'posting' ? 'bg-slate-700' : ''}
                                size="sm"
                            >
                                Posting
                            </Button>
                        </div>
                    </div>
                </div>
                
                <ReportPeriodSelector
                    mode={periodMode}
                    onModeChange={applyPeriodMode}
                    month={periodMonth}
                    year={periodYear}
                    onMonthChange={handlePeriodMonthChange}
                    onYearChange={handlePeriodYearChange}
                    dateFrom={filters.dateFrom}
                    dateTo={filters.dateTo}
                    onDateFromChange={(value) => handleCustomDateChange('dateFrom', value)}
                    onDateToChange={(value) => handleCustomDateChange('dateTo', value)}
                    effectText="AR invoices and collections inside this coverage appear here; only posted rows affect GL and Trial Balance."
                    testId="ar-summary-period"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-sm">Customer</Label>
                        <Select 
                            value={filters.customer || 'all'} 
                            onValueChange={(v) => setFilters(prev => ({ ...prev, customer: v === 'all' ? '' : v }))}
                        >
                            <SelectTrigger className="mt-1">
                                <SelectValue placeholder="All customers" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Customers</SelectItem>
                                {customers.map(c => (
                                    <SelectItem key={c.customer_id} value={c.customer_id}>
                                        {c.customer_id} - {c.customer_name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-sm">Status</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger className="mt-1">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="unposted">Unposted</SelectItem>
                                <SelectItem value="posted">Posted</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                
                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                    <Button
                        onClick={handleGenerate}
                        disabled={loading}
                        className="bg-[#2F8FCB] hover:bg-[#2577a8]"
                        data-testid="ar-summary-btn"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <Play className="w-4 h-4 mr-2" /> Preview
                            </>
                        )}
                    </Button>
                    <Button variant="outline" onClick={() => window.print()} disabled={!data}>
                        <Printer className="w-4 h-4 mr-2" /> Print
                    </Button>
                </div>
            </div>
            
            {/* Results */}
            {data && (
                <div className="space-y-4">
                    {/* Report Header */}
                    <div className="bg-[#0F2A44] text-white rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">AR Summary - {viewType === 'detailed' ? 'Detailed' : 'By Customer'}</h3>
                                <p className="text-white/70 text-sm">
                                    {data.date_from} to {data.date_to} - {dateType === 'transaction' ? 'Transaction Date' : 'Posting Date'}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-white/50">Generated</p>
                                <p className="text-sm">{new Date(data.generated_at).toLocaleString()}</p>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4" data-testid="ar-summary-where-it-appears">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-4 flex-1">
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">What changed</p>
                                    <p className="mt-1 text-sm text-slate-700">The summary was generated from AR invoices and collections for the selected filters. No source record was edited.</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Where to find it</p>
                                    <p className="mt-1 text-sm text-slate-700">Invoices stay in AR Invoices, collections stay in AR Collections, and outstanding balances reconcile to AR Aging.</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Report effect</p>
                                    <p className="mt-1 text-sm text-slate-700">Only posted AR rows affect GL and Trial Balance. Draft, submitted, returned, or cancelled rows are source evidence only.</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Next safe action</p>
                                    <p className="mt-1 text-sm text-slate-700">Open the source invoice or collection before creating corrections, returns, or additional collection evidence.</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2 shrink-0">
                                <Button type="button" variant="outline" size="sm" onClick={() => openArTrail('arinvoices', 'AR Invoices')} data-testid="ar-summary-open-invoices">
                                    AR Invoices <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={() => openArTrail('arcollections', 'AR Collections')} data-testid="ar-summary-open-collections">
                                    Collections <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={() => openArTrail('araging', 'AR Aging')} data-testid="ar-summary-open-aging">
                                    AR Aging <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </div>
                    
                    {/* Totals Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-white rounded-xl border border-blue-200 p-4">
                            <p className="text-sm text-slate-500">Total Invoiced</p>
                            <p className="text-2xl font-bold text-blue-600">{fmt(data.totals.total_invoiced)}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-emerald-200 p-4">
                            <p className="text-sm text-slate-500">Total Collected</p>
                            <p className="text-2xl font-bold text-emerald-600">{fmt(data.totals.total_collected)}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-amber-200 p-4">
                            <p className="text-sm text-slate-500">Outstanding</p>
                            <p className="text-2xl font-bold text-amber-600">{fmt(data.totals.outstanding)}</p>
                        </div>
                        {viewType === 'detailed' && (
                            <div className="bg-white rounded-xl border border-purple-200 p-4">
                                <p className="text-sm text-slate-500">Transactions</p>
                                <p className="text-2xl font-bold text-purple-600">
                                    {(data.count?.invoices || 0) + (data.count?.collections || 0)}
                                </p>
                                <p className="text-xs text-slate-400">
                                    {data.count?.invoices || 0} inv - {data.count?.collections || 0} col
                                </p>
                            </div>
                        )}
                        {viewType === 'summary' && (
                            <div className="bg-white rounded-xl border border-purple-200 p-4">
                                <p className="text-sm text-slate-500">Customers</p>
                                <p className="text-2xl font-bold text-purple-600">{data.totals.customer_count}</p>
                            </div>
                        )}
                    </div>
                    
                    {/* GL Reconciliation Panel */}
                    {data.gl_reconciliation && (
                        <div className={`rounded-lg border-2 p-4 ${
                            data.gl_reconciliation.is_reconciled 
                                ? 'bg-emerald-50 border-emerald-300' 
                                : 'bg-red-50 border-red-300'
                        }`}>
                            <div className="flex items-start gap-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    data.gl_reconciliation.is_reconciled 
                                        ? 'bg-emerald-200 text-emerald-700' 
                                        : 'bg-red-200 text-red-700'
                                }`}>
                                    {data.gl_reconciliation.is_reconciled ? (
                                        <CheckCircle2 className="w-6 h-6" />
                                    ) : (
                                        <AlertCircle className="w-6 h-6" />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <h4 className={`font-semibold ${
                                            data.gl_reconciliation.is_reconciled 
                                                ? 'text-emerald-800' 
                                                : 'text-red-800'
                                        }`}>
                                            GL Reconciliation: {data.gl_reconciliation.is_reconciled ? 'MATCHED' : 'MISMATCH'}
                                        </h4>
                                        <p className="text-xs text-slate-500 mt-1">
                                            Scope: {data.gl_reconciliation.scope === 'ar_module' ? 'AR module postings only' : 'Report totals'}
                                        </p>
                                        <span className={`text-xs px-2 py-1 rounded ${
                                            data.gl_reconciliation.is_reconciled 
                                                ? 'bg-emerald-200 text-emerald-800' 
                                                : 'bg-red-200 text-red-800'
                                        }`}>
                                            {data.gl_reconciliation.gl_entries_count} GL entries checked
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4 mt-3">
                                        <div>
                                            <p className="text-xs text-slate-500">AR Module Outstanding</p>
                                            <p className="font-semibold text-lg">{fmt(data.gl_reconciliation.ar_outstanding)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500">GL AR Control Balance</p>
                                            <p className="font-semibold text-lg">{fmt(data.gl_reconciliation.gl_ar_balance)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500">Variance</p>
                                            <p className={`font-semibold text-lg ${
                                                data.gl_reconciliation.variance === 0 
                                                    ? 'text-emerald-600' 
                                                    : 'text-red-600'
                                            }`}>
                                                {fmt(data.gl_reconciliation.variance)}
                                            </p>
                                        </div>
                                    </div>
                                    {data.reconciliation_warning && (
                                        <>
                                        <p className="mt-3 text-sm text-red-700 bg-red-100 px-3 py-2 rounded">
                                            Warning: {data.reconciliation_warning}
                                        </p>
                                        </>
                                    )}
                                    {data.manual_ar_activity_warning && (
                                        <p className="mt-3 text-sm text-amber-800 bg-amber-100 border border-amber-200 px-3 py-2 rounded">
                                            Info: {data.manual_ar_activity_warning}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Detailed View */}
                    {viewType === 'detailed' && data.transactions && (
                        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                    <tr>
                                        <th className="text-left px-4 py-3">Customer</th>
                                        <th className="text-left px-4 py-3">Doc Number</th>
                                        <th className="text-left px-4 py-3">Date</th>
                                        <th className="text-left px-4 py-3">Type</th>
                                        <th className="text-right px-4 py-3">Amount</th>
                                        <th className="text-center px-4 py-3">Status</th>
                                        <th className="text-left px-4 py-3">Bank</th>
                                        <th className="text-center px-4 py-3">Source</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {data.transactions.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="px-4 py-8 text-center text-slate-400">
                                                No transactions found for the selected criteria
                                            </td>
                                        </tr>
                                    ) : (
                                        data.transactions.map((txn, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50">
                                                <td className="px-4 py-3">
                                                    <p className="font-medium">{txn.customer_name}</p>
                                                    <p className="text-xs text-slate-400">{txn.customer_id}</p>
                                                </td>
                                                <td className="px-4 py-3 font-mono text-sm">{txn.doc_number}</td>
                                                <td className="px-4 py-3">{txn.date}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                        txn.type === 'Invoice' 
                                                            ? 'bg-blue-100 text-blue-700' 
                                                            : 'bg-emerald-100 text-emerald-700'
                                                    }`}>
                                                        {txn.type}
                                                    </span>
                                                </td>
                                                <td className={`px-4 py-3 text-right font-mono ${
                                                    txn.amount < 0 ? 'text-emerald-600' : 'text-blue-600'
                                                }`}>
                                                    {txn.amount < 0 ? `(${fmt(Math.abs(txn.amount))})` : fmt(txn.amount)}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                        txn.status === 'posted' ? 'bg-emerald-100 text-emerald-700' :
                                                        txn.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                                        'bg-amber-100 text-amber-700'
                                                    }`}>
                                                        {txn.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-slate-500">{txn.bank || '-'}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => openSummarySource(txn)}
                                                        data-testid={`ar-summary-open-source-${txn.doc_number || idx}`}
                                                        aria-label={`Open ${txn.record_type || txn.type || 'AR source'} ${txn.doc_number || ''} source evidence`}
                                                        title={`Open ${txn.record_type || txn.type || 'AR source'} ${txn.doc_number || ''} source evidence`}
                                                    >
                                                        <FileSpreadsheet className="w-4 h-4 mr-1" /> Open Source
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    {/* Summary View */}
                    {viewType === 'summary' && data.customers && (
                        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                    <tr>
                                        <th className="text-left px-4 py-3">Customer</th>
                                        <th className="text-right px-4 py-3">Total Invoiced</th>
                                        <th className="text-right px-4 py-3">Total Collected</th>
                                        <th className="text-right px-4 py-3">Outstanding</th>
                                        <th className="text-center px-4 py-3">Invoices</th>
                                        <th className="text-center px-4 py-3">Collections</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {data.customers.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="px-4 py-8 text-center text-slate-400">
                                                No customer data found for the selected criteria
                                            </td>
                                        </tr>
                                    ) : (
                                        data.customers.map((cust, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50">
                                                <td className="px-4 py-3">
                                                    <p className="font-medium">{cust.customer_name}</p>
                                                    <p className="text-xs text-slate-400">{cust.customer_id}</p>
                                                </td>
                                                <td className="px-4 py-3 text-right font-mono text-blue-600">
                                                    {fmt(cust.total_invoiced)}
                                                </td>
                                                <td className="px-4 py-3 text-right font-mono text-emerald-600">
                                                    {fmt(cust.total_collected)}
                                                </td>
                                                <td className={`px-4 py-3 text-right font-mono font-semibold ${
                                                    cust.outstanding > 0 ? 'text-amber-600' : 'text-slate-400'
                                                }`}>
                                                    {fmt(cust.outstanding)}
                                                </td>
                                                <td className="px-4 py-3 text-center text-slate-600">
                                                    {cust.invoice_count}
                                                </td>
                                                <td className="px-4 py-3 text-center text-slate-600">
                                                    {cust.collection_count}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                {data.customers.length > 0 && (
                                    <tfoot className="bg-slate-100 font-semibold">
                                        <tr>
                                            <td className="px-4 py-3">TOTAL</td>
                                            <td className="px-4 py-3 text-right font-mono text-blue-600">
                                                {fmt(data.totals.total_invoiced)}
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono text-emerald-600">
                                                {fmt(data.totals.total_collected)}
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono text-amber-600">
                                                {fmt(data.totals.outstanding)}
                                            </td>
                                            <td className="px-4 py-3 text-center">-</td>
                                            <td className="px-4 py-3 text-center">-</td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    )}
                </div>
            )}
            
            {/* Empty State */}
            {!data && !loading && (
                <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
                    <Receipt className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-500">Click "Preview" to generate AR Summary</p>
                    <p className="text-sm text-slate-400 mt-1">Source: AR Invoices and Collections</p>
                </div>
            )}
        </div>
    );
}

// ================= AR INVOICES TAB (Simplified) =================


export default ARSummaryTab;
